import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { StaticIpAssignmentDto, StaticIpDto } from '@apna/shared'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

/**
 * Which broker accounts leave from this static IP.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE IP PER ACCOUNT, SO A TICK HERE CAN MOVE ONE
 *
 * A broker whitelists one address per set of credentials, so an account is
 * either on exactly one endpoint or on none — and none is DIRECT. The server
 * enforces it with a unique key on the account, which means ticking an
 * account already assigned elsewhere MOVES it rather than adding a second
 * route.
 *
 * That is a consequential click, so the row says where the account is now
 * instead of leaving the trader to remember. Ticking one already assigned
 * elsewhere is allowed and labelled; it is a legitimate thing to want.
 *
 * The dialog sends the EXACT set rather than a diff. The list on screen is
 * the list that goes, so an account unticked is unassigned by its absence —
 * there is no second "unassign" call to forget.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function AssignStaticIpDialog({
  endpoint,
  assignments,
  onClose,
  onSave,
}: {
  endpoint: StaticIpDto | null
  assignments: StaticIpAssignmentDto[]
  onClose: () => void
  onSave: (connectionIds: string[]) => Promise<void>
}) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-seeded whenever a different endpoint is opened, so the dialog always
  // starts from what is true rather than from the last one looked at.
  useEffect(() => {
    setPicked(new Set(endpoint?.assigned.map((a) => a.connectionId) ?? []))
    setError(null)
  }, [endpoint])

  const moving = useMemo(
    () =>
      assignments.filter(
        (a) => picked.has(a.connectionId) && a.endpointId !== null && a.endpointId !== endpoint?.id,
      ),
    [assignments, picked, endpoint],
  )

  if (!endpoint) return null

  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const submit = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await onSave([...picked])
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save the assignment.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose() }}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[520px]" showCloseButton={!saving}>
        <header className="border-b border-border px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">Assign broker accounts</DialogTitle>
          <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
            Orders for the accounts you select will leave from{' '}
            <span className="num font-medium text-foreground">{endpoint.expectedExitIp ?? endpoint.label}</span>.
          </DialogDescription>
        </header>

        <div className="max-h-[320px] overflow-y-auto p-5">
          {assignments.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No broker accounts yet.</p>
          ) : (
            <ul className="grid gap-2">
              {assignments.map((a) => {
                const on = picked.has(a.connectionId)
                return (
                  <li key={a.connectionId}>
                    <button
                      type="button"
                      onClick={() => toggle(a.connectionId)}
                      aria-pressed={on}
                      data-testid={`assign-${a.connectionId}`}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                        on ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'grid size-4 shrink-0 place-items-center rounded border text-[10px] font-bold',
                          on ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                        )}
                      >
                        {on ? '✓' : ''}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium">{a.label}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {a.endpointId === null
                            ? 'No static IP assigned'
                            : a.endpointId === endpoint.id
                              ? `Currently assigned to ${endpoint.expectedExitIp ?? endpoint.label}`
                              : `Currently on ${a.expectedExitIp ?? a.endpointLabel}`}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          {/*
            Said before saving, not after. Moving an account to an address the
            broker has not whitelisted means every order is refused — and
            refused AFTER it has left, which is the state that has to be
            reconciled rather than simply refused.
          */}
          {moving.length > 0 && (
            <p role="status" className="mt-3 rounded border border-warning/25 bg-warning/5 p-2 text-[11px] text-warning">
              {moving.length === 1 ? `${moving[0]!.label} is` : `${moving.length} accounts are`} currently on
              another static IP and will be moved. Make sure this address is whitelisted with the broker first.
            </p>
          )}

          {error && (
            <p role="alert" className="mt-3 rounded border border-destructive/20 bg-destructive/5 p-2 text-[11px] text-destructive">
              {error}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
          <span className="text-[11px] text-muted-foreground">
            {picked.size === 0 ? 'No accounts — this IP carries nothing' : `${picked.size} selected`}
          </span>
          <span className="flex gap-2">
            <button type="button" disabled={saving} onClick={onClose}
              className="rounded-md border border-border px-3 py-2 text-[12px] font-medium disabled:opacity-40">
              Cancel
            </button>
            <button type="button" disabled={saving} onClick={() => void submit()} data-testid="assign-save"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12px] font-semibold text-primary-foreground disabled:opacity-50">
              {saving && <Loader2 size={13} className="animate-spin" />}
              {saving ? 'Saving…' : 'Save assignment'}
            </button>
          </span>
        </footer>
      </DialogContent>
    </Dialog>
  )
}
